import type { SchoolSummary } from '../types/tenancy';

/**
 * Platform AUTH audit sentinel (migration 0002). Not an operational school — used
 * only so append-only audit rows can satisfy the `schools` FK before a real tenant
 * context exists. Kept here (not in `audit/persist`) so browser-safe code can
 * import it without pulling in `pg`.
 */
export const PLATFORM_AUDIT_SCHOOL_ID = '00000000-0000-0000-0000-000000000000';

/**
 * Central Services tenancy unit (migration 0203, ADR-079). A governed non-Fabric
 * `schools` row: an ATR anchor, not a provision. No pupils, no URN, no MIS
 * linkage; Comms permanently off; permanently exempt from the non-Fabric
 * retirement sweep. Exactly one such unit exists — a second governed
 * non-provision unit, or a per-function split, needs an ADR-079 D6 amendment.
 */
export const CENTRAL_SERVICES_SCHOOL_ID = 'c5000000-0000-4000-8000-000000000001';

export function isCentralServicesUnit(schoolId: string): boolean {
  return schoolId === CENTRAL_SERVICES_SCHOOL_ID;
}

/**
 * Real school tenants only — excludes the platform AUTH audit sentinel (0002).
 *
 * The Central Services unit is deliberately INCLUDED: it must appear in
 * `listActiveSchoolIds()` and the school switcher, which is what puts it into
 * every estate-wide ATR read. Use {@link isProvisionSchoolId} when the question
 * is "is this a real establishment", not this one.
 */
export function isOperationalSchoolId(schoolId: string): boolean {
  return schoolId !== PLATFORM_AUDIT_SCHOOL_ID;
}

/**
 * True for rows representing a real Aurora provision (ADR-017): excludes the
 * platform AUTH sentinel AND governed non-provision tenancy units (ADR-079).
 *
 * Use this wherever "school" means *an establishment we operate or sell* — CDP
 * site pickers, price lists, CRM site projection. `isOperationalSchoolId` stays
 * the tenancy/ATR test, which the Central Services unit must pass.
 */
export function isProvisionSchoolId(schoolId: string): boolean {
  return isOperationalSchoolId(schoolId) && !isCentralServicesUnit(schoolId);
}

export function filterOperationalSchools(schools: readonly SchoolSummary[]): SchoolSummary[] {
  return schools.filter((school) => isOperationalSchoolId(school.schoolId));
}
